/**
 * InputManager - Routes data channel input messages to IPC based on coordinator permissions.
 *
 * Replaces the legacy handleIncomingMessages function for the coordinator path.
 * Permission checks use PeerCoordinator.getPeerPermissions() instead of the legacy
 * playerState store, eliminating the need to create playerState entries for each peer.
 *
 * Responsibilities:
 * - Validate incoming data channel messages as PlaycastMessage format
 * - Check per-peer permissions from the coordinator layer
 * - Check global/per-player pause state (host-level UI setting)
 * - Add transit timing metadata
 * - Route keyboard/mouse/touch directly to IPC via IIpcService
 * - Delegate gamepad setState to GamepadManager
 * - Detect admin control messages and delegate to AdminPeerHandler
 */

import type { PlaycastMessage, PlaycastMessageTarget, PlaycastMessageGamepadSetState } from '@playcastdotio/Messaging';
import { generators } from '@playcastdotio/Messaging';
import type { StateStore } from '@playcastdotio/CoreTypes';
import type { PeerPermissions } from '../types';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { PeerCoordinator } from '../PeerCoordinator';
import type { GamepadManager } from './GamepadManager';
import type { AdminPeerHandler } from '../handlers/AdminPeerHandler';
import type { PauseInputState } from '../stores/inputConfigStores';
import type { ActivityTracker } from './ActivityTracker';

export type InputManagerDeps = {
  peerCoordinator: PeerCoordinator;
  ipcService: IIpcService;
  pauseInputStore: StateStore<PauseInputState>;
  gamepadManager: GamepadManager;
  adminPeerHandler: AdminPeerHandler;
  activityTracker?: ActivityTracker;
};

export class InputManager {
  constructor(private readonly deps: InputManagerDeps) {}

  public handleMessage(peerId: string, data: unknown): void {
    if (!this.isPlaycastMessage(data)) return;

    const message = data as PlaycastMessage<PlaycastMessageTarget>;

    if (this.isPaused(peerId)) return;

    const target = message.header.target;
    const source = message.header.source;

    if (target === 'system' && this.isAdminControlMessage(message)) {
      // A reply is never a request. If a reply ever re-enters this path (e.g. a
      // transport-level loopback), reprocessing it produces another reply, which
      // loops again — an unbounded ping-pong that floods the data channel.
      if (message.header.isReply) return;

      this.deps.adminPeerHandler.handleControlMessage(peerId, message.body.message, message.header.action as string);
      return;
    }

    if (source !== 'player') return;

    if (message.header.isReply) return;

    const permissions = this.deps.peerCoordinator.getPeerPermissions(peerId);
    if (!permissions) return;

    this.deps.activityTracker?.recordActivity(peerId);

    const timed = this.addTransitTiming(message);

    // Before injection, not after: the measurement is meant to include the injection and
    // render legs, so the next encoded frame must already be attributable to this input.
    // A no-op for all but the small sampled fraction of inputs.
    this.deps.peerCoordinator.noteInputForwarded(peerId, message);

    switch (target) {
      case 'keyboard':
        if (permissions.keyboard) this.deps.ipcService.send(timed);
        break;
      case 'mouse':
        if (permissions.mouse) this.deps.ipcService.send(timed);
        break;
      case 'touch':
        if (permissions.touch) this.deps.ipcService.send(timed);
        break;
      case 'gamepad':
        this.handleGamepadMessage(peerId, permissions, timed);
        break;
      default:
        break;
    }
  }

  private handleGamepadMessage(
    peerId: string,
    permissions: PeerPermissions,
    message: PlaycastMessage<PlaycastMessageTarget>,
  ): void {
    if (message.header.action === 'setState') {
      if (!permissions.gamepad) return;
      this.deps.gamepadManager.handleGamepadMessage(
        peerId,
        message as PlaycastMessage<PlaycastMessageGamepadSetState>,
      );
    } else {
      this.deps.ipcService.send(message);
    }
  }

  private isPaused(peerId: string): boolean {
    const pauseState = this.deps.pauseInputStore.currentState;
    return pauseState.paused || !!pauseState.players[peerId]?.paused;
  }

  private isAdminControlMessage(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    const body = message.body.message as Record<string, unknown> | undefined;
    if (!body) return false;

    if (
      body.adminSessionExit === true ||
      body.launchElevatedProcess === true ||
      body.powerShellScriptChunk === true
    ) {
      return true;
    }

    const action = message.header.action as string;
    if (
      action === 'macroRecorderScreenshot' ||
      action === 'macroRecorderRecordControl' ||
      action === 'macroRecorderUploadScreenshots' ||
      action === 'macroRecorderKeepalive'
    ) {
      return true;
    }

    return false;
  }

  private isPlaycastMessage(data: unknown): boolean {
    if (!data || typeof data !== 'object') return false;
    const msg = data as Record<string, unknown>;
    return (
      typeof msg.header === 'object' &&
      msg.header !== null &&
      typeof (msg.header as Record<string, unknown>).target === 'string' &&
      typeof msg.body === 'object' &&
      msg.body !== null
    );
  }

  private addTransitTiming(message: PlaycastMessage<PlaycastMessageTarget>): PlaycastMessage<PlaycastMessageTarget> {
    const hostReceiveTime = performance.now();
    const messageCreationTime = message.body.timings?.[0]?.timestamp || Date.now();
    const transitTime = hostReceiveTime - messageCreationTime;

    return generators.addTiming(
      message,
      { timestamp: hostReceiveTime, description: 'transit_time_over_wire' },
      'host',
      {
        transit_time_ms: (Math.round(transitTime * 100) / 100).toString(),
        message_creation_time: messageCreationTime.toString(),
        host_receive_time: hostReceiveTime.toString(),
      },
    );
  }
}
